/**
 * Entry index for all JavaScript files to be used on the client facing Varian
 * Drupal site.
 *
 * @format
 */

// Essentials
import '../styles/styles.scss';

import { footer } from '@components/footer';
import { header } from '@components/header';

// Services
import { animateOdometer } from '@services/animated-numbers';
import { charts } from '@services/charts';
import { domainCheck } from '@services/domain-crawler';
import { loadAccordion } from '@services/accordion';
import { loadAnimatedLinks } from '@services/animated-link';
import { loadImageServices } from '@services/image-services';
import { loadBackToTopButton } from '@services/back-to-top';
import { loadLayoutServices } from '@services/layout-services';
import { loadScrollServices } from '@services/scroll-services';
import { startLazyLoad } from '@services/lazy-load';
import { setupOverlaps } from '@services/overlap-services';
import { setupJapanEventNotice } from '@services/ja-event-notice';
import { loadThirdPartyScripts } from '@services/third-party-services';
import { checkForDuplicateLocales } from '@services/url-services';
import { homePageServices } from '@services/home-page-services';
import { navigationSlide } from '@services/navigation-slide';

// Components
import { audioPlayback } from '@components/audio-playback';
import { cmeCredits } from '@components/cme-credits';
import { disclaimers } from '@components/disclaimers';
import { feeds } from '@components/feeds';
import { forms } from '@forms';
import { icons } from '@components/icons/';
import { modals } from '@components/modals';
import { paragraphs, imageComparisonSlider } from '@paragraphs';
import { simpleCountdownTimer } from '@components/countdown-timer/simple-countdown-timer';
import { social } from '@components/social';
import { timeline } from '@components/timeline';
import { treatmentLocationMap } from '@components/treatment-location-map';
import { annoucmentBanner } from '@components/shs-announcement-banner';

// Page and Campaigns
import { courseListing } from '@pages/courses-training/index';
import { imageGallery } from '@pages/image-gallery/';
import { demos } from '@pages/demos/demo-services';
import { newsroom } from '@pages/newsroom';
import { treatmentLocator } from '@pages/treatment-locator';
import { eventsPage } from '@pages/events';
import { searchPage } from '@pages/search';

/**
 * DOM Loaded Events.
 */
document.addEventListener('DOMContentLoaded', () => {
  const demoPage = document.querySelector('.demo-page');
  const homePage = document.querySelector('.path-frontpage');
  const path = window.location.pathname;

  /**
   * External and local scripts loaded on every page.
   */
  if (!path.startsWith('/user/')) {
    header();
    footer();
  }

  /**
   * Components.
   */
  annoucmentBanner();
  audioPlayback();
  charts();
  cmeCredits();
  disclaimers();
  forms();
  icons();
  imageComparisonSlider();
  imageGallery();
  modals();
  paragraphs();
  simpleCountdownTimer();
  social();
  timeline();
  treatmentLocationMap();

  /**
   * Services.
   */
  loadImageServices();
  animateOdometer();
  loadAccordion();
  domainCheck();
  loadLayoutServices();
  loadAnimatedLinks();
  loadBackToTopButton();
  startLazyLoad();
  setupOverlaps();
  setupJapanEventNotice();
  navigationSlide();

  /**
   * Pages & Campaigns.
   */
  eventsPage();
  newsroom();
  searchPage();

  /**
   * Course Listing.
   */
  courseListing();

  /**
   * Home page.
   */
  if (homePage) {
    homePageServices();
  }

  /**
   * Treatment Locator.
   */
  if (path.indexOf('treatment-locator') > -1) {
    treatmentLocator();
  }

  /**
   * Demo page.
   */
  if (demoPage) {
    demos();
  }
});

/**
 * Window Loaded Events.
 */
window.addEventListener('load', () => {
  setupOverlaps();
  loadThirdPartyScripts();

  setTimeout((): void => {
    feeds();
    startLazyLoad();
    loadScrollServices();
    loadImageServices();
    checkForDuplicateLocales();
  }, 500);
});
